import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAudioPlayer } from '../../contexts/AudioPlayerContext';
import { useRecordings } from '../../contexts/RecordingContext';
import { useSidebar } from '../../contexts/SidebarContext';
import { Slider } from '../ui/slider';
import {
  Play,
  Pause,
  SkipBack,
  SkipForward,
  Volume2,
  VolumeX,
  X,
  Maximize2,
  Music,
  Loader2,
  Mic,
  Radio,
  Upload,
  AlertCircle,
  Clock,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';

type PlayerMode = 'playback' | 'recording';

const GlobalAudioPlayer: React.FC = () => {
  const {
    currentRecording,
    isPlaying,
    currentTime,
    duration,
    volume,
    isMuted,
    isLoading,
    togglePlayPause,
    seek,
    setVolume: setPlayerVolume,
    toggleMute,
    skip,
    stop,
  } = useAudioPlayer();

  const { recordings } = useRecordings();
  const { collapsed } = useSidebar();
  const navigate = useNavigate();
  const [showVolumeSlider, setShowVolumeSlider] = useState(false);
  const [mode, setMode] = useState<PlayerMode>('playback');
  const [recordingElapsedTime, setRecordingElapsedTime] = useState(0);

  // Find active recording (recording, queued, or uploading status)
  const activeRecording = recordings.find(r =>
    ['recording', 'queued', 'uploading'].includes(r.status)
  );

  // Determine which mode to show
  useEffect(() => {
    if (activeRecording) {
      setMode('recording');
    } else if (currentRecording) {
      setMode('playback');
    }
  }, [activeRecording, currentRecording]);

  // Update elapsed time for active recordings
  useEffect(() => {
    if (mode === 'recording' && activeRecording) {
      const updateElapsedTime = () => {
        const startTime = activeRecording.startTime || activeRecording.start_time;
        if (!startTime) {
          setRecordingElapsedTime(0);
          return;
        }
        const elapsed = (Date.now() - new Date(startTime).getTime()) / 1000;
        setRecordingElapsedTime(Math.max(0, elapsed));
      };

      updateElapsedTime();
      const interval = setInterval(updateElapsedTime, 1000);
      return () => clearInterval(interval);
    }
  }, [mode, activeRecording]);

  // Don't show player if nothing is active
  if (!currentRecording && !activeRecording) return null;

  const formatTime = (time: number) => {
    if (isNaN(time)) return '0:00';
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const formatElapsedTime = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);

    if (hours > 0) {
      return `${hours}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getRecordingProgress = () => {
    if (!activeRecording) return 0;
    const capturedMinutes = activeRecording.capturedMinutes || activeRecording.captured_minutes || 0;
    const totalMinutes = activeRecording.maxDuration || activeRecording.max_duration || 60;
    return Math.min(100, (capturedMinutes / totalMinutes) * 100);
  };

  const getStatusInfo = (status: string) => {
    switch (status) {
      case 'recording':
        return {
          icon: Mic,
          color: 'text-red-600 dark:text-red-400',
          bgColor: 'bg-red-500/20',
          label: 'Recording',
          pulse: true,
        };
      case 'queued':
        return {
          icon: Radio,
          color: 'text-yellow-600 dark:text-yellow-400',
          bgColor: 'bg-yellow-500/20',
          label: 'Queued',
          pulse: false,
        };
      case 'uploading':
        return {
          icon: Upload,
          color: 'text-blue-600 dark:text-blue-400',
          bgColor: 'bg-blue-500/20',
          label: 'Uploading',
          pulse: false,
        };
      default:
        return {
          icon: AlertCircle,
          color: 'text-gray-600 dark:text-gray-400',
          bgColor: 'bg-gray-500/20',
          label: status,
          pulse: false,
        };
    }
  };

  const handleNavigateToRecording = () => {
    if (mode === 'playback' && currentRecording) {
      navigate(`/recordings/${currentRecording.id}`);
    } else if (mode === 'recording' && activeRecording) {
      navigate(`/recordings/${activeRecording.id}`);
    }
  };

  // Recording mode render
  if (mode === 'recording' && activeRecording) {
    const statusInfo = getStatusInfo(activeRecording.status);
    const StatusIcon = statusInfo.icon;
    const progress = getRecordingProgress();
    const capturedMinutes = activeRecording.capturedMinutes || activeRecording.captured_minutes || 0;
    const totalMinutes = activeRecording.maxDuration || activeRecording.max_duration || 60;

    return (
      <AnimatePresence>
        <motion.div
          className={`fixed bottom-0 left-0 right-0 bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 shadow-2xl z-40 ${
            collapsed ? 'lg:left-16' : 'lg:left-64'
          }`}
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        >
          {/* Progress bar at the top of player */}
          <div className="relative h-1 bg-gray-200 dark:bg-gray-700">
            <motion.div
              className={`absolute inset-y-0 left-0 ${
                activeRecording.status === 'recording'
                  ? 'bg-gradient-to-r from-red-500 to-pink-500'
                  : activeRecording.status === 'uploading'
                  ? 'bg-gradient-to-r from-blue-500 to-cyan-500'
                  : 'bg-gradient-to-r from-yellow-500 to-orange-500'
              }`}
              style={{ width: `${progress}%` }}
              animate={
                statusInfo.pulse
                  ? {
                      opacity: [1, 0.7, 1],
                    }
                  : {}
              }
              transition={{
                duration: 2,
                repeat: statusInfo.pulse ? Infinity : 0,
              }}
            />
          </div>

          {/* Player content */}
          <div className="container mx-auto px-4 py-3">
            <div className="flex items-center justify-between gap-4">
              {/* Left: Recording info */}
              <div className="flex items-center gap-3 flex-1 min-w-0">
                {/* Status indicator */}
                <motion.div
                  className={`w-14 h-14 rounded-md ${statusInfo.bgColor} flex items-center justify-center flex-shrink-0 cursor-pointer hover:scale-105 transition-transform`}
                  whileHover={{ scale: 1.05 }}
                  onClick={handleNavigateToRecording}
                  animate={
                    statusInfo.pulse
                      ? {
                          scale: [1, 1.05, 1],
                        }
                      : {}
                  }
                  transition={{
                    duration: 2,
                    repeat: statusInfo.pulse ? Infinity : 0,
                  }}
                >
                  <StatusIcon className={`h-6 w-6 ${statusInfo.color}`} />
                </motion.div>

                {/* Recording info */}
                <div className="min-w-0 flex-1">
                  <motion.button
                    onClick={handleNavigateToRecording}
                    className="text-left w-full hover:underline"
                    whileHover={{ x: 2 }}
                  >
                    <h4 className="font-semibold text-gray-900 dark:text-gray-100 truncate">
                      {activeRecording.title || 'Untitled Recording'}
                    </h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400 truncate">
                      {activeRecording.venueName || activeRecording.venue_name || 'Unknown Venue'}
                    </p>
                  </motion.button>
                </div>
              </div>

              {/* Center: Recording status and time */}
              <div className="flex flex-col items-center gap-2 flex-shrink-0">
                {/* Status badge */}
                <Badge
                  variant={activeRecording.status === 'recording' ? 'default' : 'outline'}
                  className={`${statusInfo.color} ${statusInfo.bgColor}`}
                >
                  <StatusIcon className="h-3 w-3 mr-1" />
                  {statusInfo.label}
                </Badge>

                {/* Elapsed time and progress */}
                <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
                  <Clock className="h-3.5 w-3.5" />
                  <span className="font-mono">{formatElapsedTime(recordingElapsedTime)}</span>
                  <span>•</span>
                  <span>{capturedMinutes.toFixed(1)} / {totalMinutes} min</span>
                </div>
              </div>

              {/* Right: Actions */}
              <div className="flex items-center gap-2 flex-1 justify-end">
                {/* Expand button */}
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100"
                  onClick={handleNavigateToRecording}
                  title="View recording details"
                >
                  <Maximize2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>
    );
  }

  // Playback mode render
  if (!currentRecording) return null;

  return (
    <AnimatePresence>
      <motion.div
        className={`fixed bottom-0 left-0 right-0 bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 shadow-2xl z-40 ${
          collapsed ? 'lg:left-16' : 'lg:left-64'
        }`}
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 100, opacity: 0 }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
      >
        {/* Progress bar at the top of player */}
        <div className="relative h-1 bg-gray-200 dark:bg-gray-700">
          <motion.div
            className="absolute inset-y-0 left-0 bg-gradient-to-r from-purple-500 to-pink-500"
            style={{ width: `${duration ? (currentTime / duration) * 100 : 0}%` }}
            initial={{ width: 0 }}
            animate={{ width: `${duration ? (currentTime / duration) * 100 : 0}%` }}
          />
          <input
            type="range"
            min="0"
            max={duration || 100}
            value={currentTime}
            onChange={(e) => seek(Number(e.target.value))}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          />
        </div>

        {/* Player content */}
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center justify-between gap-4">
            {/* Left: Recording info */}
            <div className="flex items-center gap-3 flex-1 min-w-0">
              {/* Album art placeholder */}
              <motion.div
                className="w-14 h-14 rounded-md bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center flex-shrink-0 cursor-pointer hover:scale-105 transition-transform"
                whileHover={{ scale: 1.05 }}
                onClick={handleNavigateToRecording}
              >
                <Music className="h-6 w-6 text-white" />
              </motion.div>

              {/* Track info */}
              <div className="min-w-0 flex-1">
                <motion.button
                  onClick={handleNavigateToRecording}
                  className="text-left w-full hover:underline"
                  whileHover={{ x: 2 }}
                >
                  <h4 className="font-semibold text-gray-900 dark:text-gray-100 truncate">
                    {currentRecording.title}
                  </h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400 truncate">
                    {currentRecording.venueName || currentRecording.venue_name || 'Unknown Venue'}
                  </p>
                </motion.button>
              </div>
            </div>

            {/* Center: Playback controls */}
            <div className="flex flex-col items-center gap-2 flex-shrink-0">
              {/* Control buttons */}
              <div className="flex items-center gap-3">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100"
                  onClick={() => skip(-10)}
                >
                  <SkipBack className="h-4 w-4" />
                </Button>

                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  <Button
                    size="icon"
                    className="h-10 w-10 rounded-full bg-purple-600 hover:bg-purple-700 text-white shadow-lg"
                    onClick={togglePlayPause}
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <Loader2 className="h-5 w-5 animate-spin" />
                    ) : isPlaying ? (
                      <Pause className="h-5 w-5" />
                    ) : (
                      <Play className="h-5 w-5 ml-0.5" />
                    )}
                  </Button>
                </motion.div>

                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100"
                  onClick={() => skip(10)}
                >
                  <SkipForward className="h-4 w-4" />
                </Button>
              </div>

              {/* Time display */}
              <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
                <span>{formatTime(currentTime)}</span>
                <span>/</span>
                <span>{formatTime(duration)}</span>
              </div>
            </div>

            {/* Right: Volume and actions */}
            <div className="flex items-center gap-2 flex-1 justify-end">
              {/* Volume control */}
              <div
                className="flex items-center gap-2 relative"
                onMouseEnter={() => setShowVolumeSlider(true)}
                onMouseLeave={() => setShowVolumeSlider(false)}
              >
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100"
                  onClick={toggleMute}
                >
                  {isMuted || volume === 0 ? (
                    <VolumeX className="h-4 w-4" />
                  ) : (
                    <Volume2 className="h-4 w-4" />
                  )}
                </Button>

                <AnimatePresence>
                  {showVolumeSlider && (
                    <motion.div
                      className="absolute bottom-full left-0 mb-2 bg-white dark:bg-gray-800 p-2 rounded-lg shadow-xl"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 10 }}
                    >
                      <div className="w-24">
                        <Slider
                          value={[isMuted ? 0 : volume]}
                          onValueChange={(value) => setPlayerVolume(value[0])}
                          max={1}
                          step={0.01}
                          className="w-full"
                        />
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Expand button */}
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100"
                onClick={handleNavigateToRecording}
                title="Open full view"
              >
                <Maximize2 className="h-4 w-4" />
              </Button>

              {/* Close button */}
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-gray-700 dark:text-gray-300 hover:text-red-600 dark:hover:text-red-400"
                onClick={stop}
                title="Close player"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>

        {/* Waveform visualization (optional) */}
        {currentRecording.waveformData && currentRecording.waveformData.length > 0 && (
          <div className="absolute bottom-full left-0 right-0 h-16 flex items-end gap-0.5 px-4 pb-1 opacity-20 pointer-events-none">
            {currentRecording.waveformData.slice(0, 100).map((height, index) => (
              <motion.div
                key={index}
                className="flex-1 bg-gradient-to-t from-purple-500 to-pink-500 rounded-t-sm"
                style={{
                  height: `${height}%`,
                  opacity: currentTime && duration ? (index / 100 < currentTime / duration ? 1 : 0.3) : 0.3
                }}
                animate={isPlaying ? {
                  height: [`${height}%`, `${Math.min(100, height * 1.2)}%`, `${height}%`],
                } : {}}
                transition={{
                  duration: 0.5 + Math.random(),
                  repeat: isPlaying ? Infinity : 0,
                  ease: 'easeInOut',
                }}
              />
            ))}
          </div>
        )}
      </motion.div>
    </AnimatePresence>
  );
};

export default GlobalAudioPlayer;
